import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, Brain, Cpu, Shield, Network, Database, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import techHeroImage from '@/assets/tech-hero-bg.jpg';

const Hero = () => {
  const { t } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const nodesRef = useRef<any[]>([]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Neural Network Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create neural network nodes
    const createNodes = () => {
      const nodes = [];
      const nodeCount = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
      
      for (let i = 0; i < nodeCount; i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          connections: [],
          pulse: Math.random() * Math.PI * 2,
        });
      }
      return nodes;
    };

    nodesRef.current = createNodes();

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update nodes
      nodesRef.current.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.02;

        // Boundary collision
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        // Keep in bounds
        node.x = Math.max(0, Math.min(canvas.width, node.x));
        node.y = Math.max(0, Math.min(canvas.height, node.y));

        // Friction
        node.vx *= 0.99;
        node.vy *= 0.99;

        // Find connections
        node.connections = [];
        nodesRef.current.forEach((otherNode, j) => {
          if (i !== j) {
            const dist = Math.sqrt(
              Math.pow(node.x - otherNode.x, 2) + Math.pow(node.y - otherNode.y, 2)
            );
            if (dist < 120) {
              node.connections.push({ node: otherNode, distance: dist });
            }
          }
        });
      });

      // Draw connections
      nodesRef.current.forEach(node => {
        node.connections.forEach(({ node: connectedNode, distance }) => {
          const opacity = Math.max(0, (120 - distance) / 120) * 0.5;
          const gradient = ctx.createLinearGradient(node.x, node.y, connectedNode.x, connectedNode.y);
          gradient.addColorStop(0, `hsla(180, 100%, 50%, ${opacity})`);
          gradient.addColorStop(0.5, `hsla(300, 100%, 70%, ${opacity * 1.5})`);
          gradient.addColorStop(1, `hsla(180, 100%, 50%, ${opacity})`);
          
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y);
          ctx.lineTo(connectedNode.x, connectedNode.y);
          ctx.stroke();
        });
      });

      // Draw nodes
      nodesRef.current.forEach(node => {
        const pulse = Math.sin(node.pulse) * 0.5 + 0.5;
        const size = node.size + pulse * 2;
        
        // Node glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, size * 3);
        gradient.addColorStop(0, `hsla(180, 100%, 50%, ${0.8 + pulse * 0.2})`);
        gradient.addColorStop(0.3, `hsla(300, 100%, 70%, ${0.4 + pulse * 0.2})`);
        gradient.addColorStop(1, 'hsla(180, 100%, 50%, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size * 3, 0, Math.PI * 2);
        ctx.fill();

        // Node core
        ctx.fillStyle = `hsla(180, 100%, 50%, ${0.9 + pulse * 0.1})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);


  const floatingElements = [
    { icon: Brain, delay: '0s', position: 'top-20 left-20', color: 'text-primary' },
    { icon: Shield, delay: '1s', position: 'top-32 right-32', color: 'text-accent' },
    { icon: Network, delay: '2s', position: 'bottom-40 left-16', color: 'text-primary' },
    { icon: Database, delay: '0.5s', position: 'bottom-20 right-20', color: 'text-accent' },
    { icon: Lock, delay: '1.5s', position: 'top-1/2 right-12', color: 'text-primary' },
    { icon: Cpu, delay: '2.5s', position: 'bottom-1/3 left-32', color: 'text-accent' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-hero">
      {/* Dynamic Neural Network Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ background: 'transparent' }}
      />
      
      {/* Animated Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{ backgroundImage: `url(${techHeroImage})` }}
      />
      
      {/* Neural Network Grid Pattern */}
      <div className="absolute inset-0 bg-pattern-grid opacity-10" style={{ backgroundSize: '50px 50px' }} />
      
      {/* Floating Tech Elements */}
      {floatingElements.map((element, index) => {
        const IconComponent = element.icon;
        return (
          <div
            key={index}
            className={`absolute ${element.position} opacity-20 animate-float`}
            style={{ animationDelay: element.delay }}
          >
            <div className="p-4 rounded-full bg-gradient-surface backdrop-blur-sm border border-primary/20">
              <IconComponent className={`h-8 w-8 ${element.color}`} />
            </div>
          </div>
        );
      })}

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-glow-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-accent/20 rounded-full blur-xl animate-glow-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 text-center">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div 
            className={`inline-flex items-center space-x-2 bg-card/30 backdrop-blur-md border border-primary/30 rounded-full px-6 py-3 mb-8 shadow-tech transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
          >
            <Zap className="h-5 w-5 text-primary animate-glow-pulse" />
            <span className="text-sm font-medium text-primary">{t('hero.badge')}</span>
          </div>

          {/* Main Heading */}
          <h1 className={`text-5xl md:text-7xl lg:text-8xl font-bold mb-8 transform transition-all duration-1000 delay-300 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <span className="bg-gradient-tech bg-clip-text text-transparent">
              {t('hero.title.quantum')}
            </span>
            <span className="text-foreground">{t('hero.title.box')}</span>
            <br />
            <span className="bg-gradient-tech bg-clip-text text-transparent">{t('hero.title.ai')}</span>
          </h1>

          {/* Dynamic Architecture Tagline */}
          <div className={`mb-8 transform transition-all duration-1000 delay-500 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <p className="text-xl md:text-3xl text-muted-foreground mb-4 max-w-4xl mx-auto leading-relaxed">
              <span className="text-primary font-semibold">{t('hero.tagline.1')}</span> • 
              <span className="text-accent font-semibold"> {t('hero.tagline.2')}</span> • 
              <span className="text-primary font-semibold"> {t('hero.tagline.3')}</span>
            </p>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('hero.description')}
            </p>
          </div>

          {/* CTA Buttons Grid */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-16 transform transition-all duration-1000 delay-700 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <Link to="/contact">
              <Button variant="hero" size="lg" className="w-full text-lg py-6">
                {t('hero.cta.initialize')}
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
            <Link to="/assessment">
              <Button variant="cta" size="lg" className="w-full text-lg py-6">
                {t('hero.cta.assessment')}
              </Button>
            </Link>
            <Link to="/rpa-assessment">
              <Button variant="outline" size="lg" className="w-full text-lg py-6 bg-accent/10 border-accent/30 text-accent hover:bg-accent/20">
                {t('hero.cta.automation')}
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" size="lg" className="w-full text-lg py-6">
                {t('hero.cta.explore')}
              </Button>
            </Link>
          </div>

          {/* Tech Stats */}
          <div className={`grid grid-cols-1 md:grid-cols-4 gap-8 max-w-4xl mx-auto transform transition-all duration-1000 delay-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-3 bg-gradient-surface rounded-lg border border-primary/20 shadow-tech">
                  <Brain className="h-8 w-8 text-primary" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">99.9%</div>
              <div className="text-sm text-muted-foreground">{t('hero.stats.neural')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-3 bg-gradient-surface rounded-lg border border-accent/20 shadow-tech">
                  <Zap className="h-8 w-8 text-accent" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">10x</div>
              <div className="text-sm text-muted-foreground">{t('hero.stats.processing')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-3 bg-gradient-surface rounded-lg border border-primary/20 shadow-tech">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">256-bit</div>
              <div className="text-sm text-muted-foreground">{t('hero.stats.security')}</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="p-3 bg-gradient-surface rounded-lg border border-accent/20 shadow-tech">
                  <Cpu className="h-8 w-8 text-accent" />
                </div>
              </div>
              <div className="text-3xl font-bold bg-gradient-tech bg-clip-text text-transparent mb-2">24/7</div>
              <div className="text-sm text-muted-foreground">{t('hero.stats.operation')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Stream Animation */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50">
        <div className="h-full w-20 bg-gradient-to-r from-transparent via-primary to-transparent animate-[slide-in_3s_ease-in-out_infinite]" />
      </div>
    </section>
  );
};

export default Hero;